// Favourites API client. All calls hit main-service's authenticated gateway,
// which forwards user identity to mixgenie-service and enforces auth.
//
// Route shape on mixgenie-service: /:app/user/favourites/... (see
// mixgenie-service/src/modules/favourites/favourites.controller.ts).

import type { CatalogSong } from '../catalog/api';

const mainServiceURL: string = import.meta.env.VITE_MAIN_SERVICE_URL || '';
const publicGatewayService: string =
  import.meta.env.VITE_MIXGENIE_PUBLIC_GATEWAY_SERVICE || 'mixgenie-service';

async function callAuthed(path: string, options: { method?: string; body?: unknown } = {}) {
  const headers: HeadersInit = {};
  if (options.body) headers['Content-Type'] = 'application/json';

  const response = await fetch(
    `${mainServiceURL}/gateway/${publicGatewayService}/user/favourites/${path.replace(/^\//, '')}`,
    {
      method: options.method || 'GET',
      headers,
      credentials: 'include', // send accessToken cookie for main-service auth
      body: options.body ? JSON.stringify(options.body) : undefined,
    },
  );

  if (response.status === 401 || response.status === 403) {
    const err: any = new Error('Unauthenticated');
    err.status = response.status;
    throw err;
  }

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(json?.message || json?.error || 'Favourites request failed');
  }
  return json;
}

export async function toggleFavourite(songId: string): Promise<{ favourited: boolean }> {
  return callAuthed(encodeURIComponent(songId), { method: 'POST' });
}

export async function removeFavourite(songId: string): Promise<{ favourited: boolean }> {
  return callAuthed(encodeURIComponent(songId), { method: 'DELETE' });
}

export type FavouritesPage = {
  items: Array<{
    _id: string;
    createdAt: string;
    song: Pick<
      CatalogSong,
      | '_id'
      | 'songTitle'
      | 'slug'
      | 'artistName'
      | 'artistSlug'
      | 'albumName'
      | 'media'
      | 'youtubeId'
      | 'sourceLanguage'
    > & { songDuration?: number };
  }>;
  total: number;
  page: number;
  limit: number;
};

export async function listFavourites(page = 1, limit = 24): Promise<FavouritesPage> {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  return callAuthed(`?${qs.toString()}`);
}

export async function lookupFavourites(songIds: string[]): Promise<Record<string, true>> {
  if (!songIds.length) return {};
  return callAuthed('lookup', { method: 'POST', body: { songIds } });
}
